import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { getChatbots, ChatBot } from "../../services/dashboard/GetChatBots";
import "../../styles/dash/DashBoard.css";
import NavDashBoard from "./NavDashBoard";
import SideBar from "./SideBar";
import CreateOrEditChatBot from "./CreateOrEditChatBot";
import { changeChatBotStatus } from "../../services/dashboard/ChangeStatus";
import LoadingSpinner from "../utility/LoadingSpinner";
import Roles from "./Roles";
import DemoRequestTable from "./DemoRequestTable";
import BotsSection from "./BotsSection";
import TestTable from "./TestTable";
import Users from "./Users"
import Profile from "./Profile";
import Clients from "./Clients";
import Blogs from "./Blogs";
import SpiderWebBackground from "../app/SpiderWebBackground";


const IconChatBot = () => (
  <span className="sidebar-icon">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  </span>
);
const IconDemoRequest = () => (
  <span className="sidebar-icon">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  </span>
);
const IconUsers = () => (
  <span className="sidebar-icon">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M20 21v-2a4 4 0 0 0-3-3.87M4 21v-2a4 4 0 0 1 3-3.87M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0z" />
    </svg>
  </span>
);
const IconRoles = () => (
  <span className="sidebar-icon">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  </span>
);
const IconTestTable = () => (
  <span className="sidebar-icon">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="3" y1="9" x2="21" y2="9"></line>
      <line x1="3" y1="15" x2="21" y2="15"></line>
      <line x1="9" y1="3" x2="9" y2="21"></line>
      <line x1="15" y1="3" x2="15" y2="21"></line>
    </svg>
  </span>
);
const IconClients = () => (
  <span className="sidebar-icon">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  </span>
)
const IconBlogs = () => (
  <span className="sidebar-icon">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
    </svg>
  </span>
)


interface Permission {
  Id: number;
  IdAction: number;
  Action: string;
  IdModule: number;
  ModuleName: string;
}

interface Role {
  Id: number;
  RoleName: string;
  Permissions: Permission[];
}

interface UserData {
  User: string;
  Email: string;
  roles: Role[];
}

const Dashboard: React.FC = () => {
  // Calculate initial route and permissions once
  const { initialRoute, permissions: initialPermissions, userData: initialUserData } = useMemo(() => {
    const userString = localStorage.getItem("user");
    let permissions: number[] = [];
    let userData: UserData | null = null;
    let route: string | null = null;

    if (userString) {
      try {
        const userObject = JSON.parse(JSON.parse(userString).userData || "{}");
        if (userObject?.roles?.length) {
          permissions = userObject.roles.flatMap((rol: Role) =>
            rol.Permissions.map((permiso: Permission) => permiso.IdAction)
          );
          userData = {
            User: userObject.User || "Usuario",
            Email: userObject.Email || "Email",
            roles: userObject.roles || [],
          };
        }
      } catch (e) {
        console.error("Error parsing user data for initial route", e);
      }
    }

    if (permissions.includes(7)) route = "bots";
    else if (permissions.includes(32)) route = "demo-request";
    else if (permissions.includes(23)) route = "clients";
    else if (permissions.includes(28)) route = "blogs";
    else if (permissions.includes(12)) route = "users";
    else if (permissions.includes(18)) route = "roles";

    return { initialRoute: route, permissions, userData };
  }, []);

  const [allBots, setAllBots] = useState<ChatBot[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [botSeleccionado, setBotSeleccionado] = useState<ChatBot | null>(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [permisos] = useState<number[]>(initialPermissions);
  const [userData] = useState<UserData | null>(initialUserData);
  const [mostrarTabla, setMostrarTabla] = useState<boolean>(true);
  const [currentCardPage, setCurrentCardPage] = useState<number>(1);
  const [globalFilter, setGlobalFilter] = useState<string>("");

  const location = useLocation();

  const getPageTitle = () => {
    const path = location.pathname;
    const Title = ({ children }: { children: React.ReactNode }) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {children}
      </div>
    );
    if (path.includes("/bots")) return <Title><IconChatBot /> ChatBots</Title>;
    if (path.includes("/demo-request")) return <Title><IconDemoRequest /> Solicitudes de Demo</Title>;
    if (path.includes("/users")) return <Title><IconUsers /> Gestión de Usuarios</Title>;
    if (path.includes("/roles")) return <Title><IconRoles /> Administración de Roles</Title>;
    if (path.includes("/test-table")) return <Title><IconTestTable /> Tabla de Pruebas</Title>;
    if (path.includes("/profile")) return <Title><IconUsers /> Perfil</Title>;
    if (path.includes("/clients")) return <Title><IconClients /> Clientes</Title>;
    if (path.includes("/blogs")) return <Title><IconBlogs /> Gestion de Blogs</Title>;
    //return (
    //  )
    //)
    return "Dashboard";
  };

  // Recuperar estado del localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('botsSectionState');
    if (savedState) {
      try {
        const { mostrarTabla, currentCardPage, globalFilter } = JSON.parse(savedState);
        setMostrarTabla(mostrarTabla);
        setCurrentCardPage(currentCardPage);
        setGlobalFilter(globalFilter);
      } catch (e) {
        console.error("Failed to parse botsSectionState", e);
      }
    }
  }, []);

  // Guardar estado en localStorage cuando cambie
  useEffect(() => {
    const state = {
      mostrarTabla,
      currentCardPage,
      globalFilter
    };
    localStorage.setItem('botsSectionState', JSON.stringify(state));
  }, [mostrarTabla, currentCardPage, globalFilter]);

  const loadBots = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getChatbots();
      setAllBots(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Solo cargar bots si estamos en la sección de bots
    if (location.pathname.endsWith("/bots")) {
      loadBots();
    }
  }, [location.pathname, loadBots]);

  const toggleBotStatus = async (id: number) => {
    setLoading(true);

    const botIndex = allBots.findIndex((b) => b.id === id);
    if (botIndex === -1) return;

    const bot = allBots[botIndex];
    const newStatus = bot.status === 1 ? 0 : 1;

    try {
      await changeChatBotStatus(id, newStatus);

      // Actualizar solo el bot modificado
      setAllBots(prevBots => {
        const updatedBots = [...prevBots];
        updatedBots[botIndex] = {
          ...bot,
          status: newStatus
        };
        return updatedBots;
      });
    } catch (error) {
      alert("No se pudo cambiar el estado del bot.");
    } finally {
      setLoading(false);
    }
  };


function ofuscarCadenaXOR(texto: string, clave: string): string {
  let resultado = '';
  // Convertimos la clave a un array de códigos de caracteres para el cifrado
  const claveBytes = clave.split('').map(c => c.charCodeAt(0));
  const claveLongitud = claveBytes.length;

  for (let i = 0; i < texto.length; i++) {
    // Obtenemos el código de caracter del texto y de la clave
    const textoCharCode = texto.charCodeAt(i);
    const claveCharCode = claveBytes[i % claveLongitud];
    
    // Realizamos la operación XOR y convertimos el resultado a un caracter
    resultado += String.fromCharCode(textoCharCode ^ claveCharCode);
  }

  return resultado;
}

  return (
    <div className="dashboard-wrapper">
      {isLoading && <LoadingSpinner />}

      <SideBar
        name={userData?.User || "Usuario"}
        email={userData?.Email || "Email"}
        permisos={permisos}
      />

      <div className="dashboard-main">
        <NavDashBoard title={getPageTitle()} />



        <Routes>
          <Route index element={initialRoute ? <Navigate to={initialRoute} replace /> : null} />

          <Route path="bots" element={
            <BotsSection
              bots={allBots}
              permisos={permisos}
              toggleBotStatus={toggleBotStatus}
              setBotSeleccionado={setBotSeleccionado}
              setMostrarFormulario={setMostrarFormulario}
              refreshBots={loadBots}
              // Nuevos props para manejar el estado de la sección de bots
              mostrarTabla={mostrarTabla}
              setMostrarTabla={setMostrarTabla}
              currentCardPage={currentCardPage}
              setCurrentCardPage={setCurrentCardPage}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
            />
          } />

          <Route path="roles" element={<Roles />} />

          <Route path="demo-request" element={<DemoRequestTable />} />

          <Route path="test-table" element={<TestTable />} />

          <Route path="users" element={<Users />} />

          <Route path="profile" element={<Profile />} />

          <Route path="clients" element={<Clients />} />

          <Route path="blogs" element={<Blogs />} />
        </Routes>

        {mostrarFormulario && location.pathname.endsWith("/bots") && (
          <CreateOrEditChatBot
            bot={botSeleccionado}
            onClose={() => {
              setMostrarFormulario(false);
              setBotSeleccionado(null);
            }}
            onSuccess={loadBots}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;